// PMTiles-Direkter DEM-Sampler (Wave 14cf)
// =========================================
//
// Liest Höhenwerte direkt aus den hillshade-PMTiles im OPFS — unabhängig
// vom Map-Viewport. Ergänzt `map.queryTerrainElevation()`, das nur Tiles
// sieht die MapLibre gerade rendert.
//
// Warum nicht via queryTerrainElevation reichen?
//   queryTerrainElevation gibt nur dann brauchbare Werte zurück, wenn die
//   DEM-Tiles für die abgefragte Position aktuell in MapLibre's Tile-Cache
//   geladen sind. Bei einer 73 km Custom-Tour zoomed der Viewport meist
//   nur auf einen Bruchteil der Strecke — Samples für entfernte Trail-
//   Bereiche returnen null, der Sampler verwirft das Profil (≥30%-Hürde),
//   und das System fällt auf den HTTP-Fallback (opentopodata) zurück.
//   Online-Pfad obwohl die SRTM-Bytes lokal verfügbar sind.
//
// Was dieses Modul macht:
//   1. Bestimmt aus lat/lon die passende Region-PMTiles (Liguria oder Elba).
//   2. Berechnet Web-Mercator-Tile-Coords (z, x, y) bei z=12 (= max-zoom
//      unserer Hillshade-PMTiles, siehe scripts/generate-pmtiles.sh).
//   3. Liest den Tile-Buffer via PMTiles.getZxy() — direkt aus OPFS, kein
//      MapLibre-Roundtrip.
//   4. Decodiert PNG → RGB-Pixel (createImageBitmap + OffscreenCanvas).
//   5. Mapbox-RGB-Formel: elev = -10000 + (R*65536 + G*256 + B) * 0.1
//   6. LRU-Cache pro Tile (max 64 Tiles ≈ 16 MB Memory).
//
// Performance: pro Tile ~30-80 ms (PNG-Decode auf iPhone). Ein 73 km-
// Trail kreuzt typisch 6-12 Tiles bei z=12; total Sampling für 100
// Punkte: <1 Sekunde, alle Folge-Sampling unter 50 ms (Cache-Hit).

import type { PMTiles } from 'pmtiles';
import { ELBA_BOUNDS, LIGURIA_BOUNDS } from '@adtu/shared';
import { getPmtilesArchive } from '../map/pmtiles';

// Z-Level der Hillshade-PMTiles. Muss matchen mit `--max-z` in
// `rio rgbify` Aufrufen in scripts/generate-pmtiles.sh (= 12).
const HILLSHADE_MAX_Z = 12;
const TILE_SIZE = 256;

// LRU-Cache für decoded RGBA-Pixel-Buffer pro Tile. Key = `${region}/${z}/${x}/${y}`.
// Wert = Uint8ClampedArray (TILE_SIZE * TILE_SIZE * 4 = 256 KB pro Tile,
// bei 64 Slots also ~16 MB Memory worst case — auf modernen Phones ok).
const TILE_CACHE_MAX = 64;
type TileCacheEntry = {
  pixels: Uint8ClampedArray;
  width: number;
  height: number;
};
const tileCache = new Map<string, TileCacheEntry>();

/**
 * In-Flight-Dedup: wenn zwei sample()-Aufrufe denselben Tile gleichzeitig
 * brauchen, soll nur EIN PNG-Decode laufen. Map<key, Promise>.
 */
const inFlightDecodes = new Map<string, Promise<TileCacheEntry | null>>();

/**
 * Ergebnis einer Region-Resolution. `archiveUrl` ist null wenn weder
 * Liguria noch Elba die Position einschliesst (z. B. User panst nach
 * Norden, Festland-Italien) — dann gibt's offline keine SRTM-Daten und
 * der Caller muss auf den nächsten Pfad in der Pipeline.
 */
interface ResolvedTile {
  archiveUrl: string;
  region: 'liguria' | 'elba';
}

/**
 * Bestimmt aus einer lat/lon-Position welche Region-PMTiles wir brauchen.
 * Bevorzugt Elba wenn die Position in BEIDEN Bounds liegt (sollte nicht
 * vorkommen, sind disjunkt, aber defensiv).
 */
function resolveRegionForPosition(
  lat: number,
  lon: number,
): ResolvedTile | null {
  const inElba =
    lon >= ELBA_BOUNDS.west &&
    lon <= ELBA_BOUNDS.east &&
    lat >= ELBA_BOUNDS.south &&
    lat <= ELBA_BOUNDS.north;
  if (inElba) {
    return {
      archiveUrl: hillshadeArchiveUrl('elba'),
      region: 'elba',
    };
  }
  const inLiguria =
    lon >= LIGURIA_BOUNDS.west &&
    lon <= LIGURIA_BOUNDS.east &&
    lat >= LIGURIA_BOUNDS.south &&
    lat <= LIGURIA_BOUNDS.north;
  if (inLiguria) {
    return {
      archiveUrl: hillshadeArchiveUrl('liguria'),
      region: 'liguria',
    };
  }
  return null;
}

/**
 * Baut die `pmtiles://`-Style-URL für eine Region. Muss EXAKT der URL
 * matchen die `MapView.tsx` an `addPmtilesArchive()` übergibt — sonst
 * findet `getPmtilesArchive()` die Instanz nicht.
 */
function hillshadeArchiveUrl(region: 'liguria' | 'elba'): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/tiles/${region}_hillshade.pmtiles`;
}

/**
 * Web-Mercator-Tile-Coords aus lat/lon bei einem gegebenen Zoom.
 *
 * Zusatz: gibt auch die Pixel-Position innerhalb des Tiles zurück
 * (subTileX, subTileY ∈ [0, TILE_SIZE)), damit wir bilinear sampeln können.
 */
function latLonToTileAndPixel(
  lat: number,
  lon: number,
  zoom: number,
): { z: number; x: number; y: number; subX: number; subY: number } | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat <= -85 || lat >= 85) return null; // outside Web Mercator

  const n = 2 ** zoom;
  const xWorld = ((lon + 180) / 360) * n * TILE_SIZE;
  const latRad = (lat * Math.PI) / 180;
  const yWorld =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
    n *
    TILE_SIZE;

  const tileX = Math.floor(xWorld / TILE_SIZE);
  const tileY = Math.floor(yWorld / TILE_SIZE);
  const subX = xWorld - tileX * TILE_SIZE;
  const subY = yWorld - tileY * TILE_SIZE;
  return { z: zoom, x: tileX, y: tileY, subX, subY };
}

/**
 * Lädt einen Tile-Buffer aus dem PMTiles-Archive, decodiert das PNG und
 * cached die RGBA-Pixel. Returns null wenn:
 *   - das Archive nicht registriert ist (PMTiles fehlt offline)
 *   - der Tile in der PMTiles nicht existiert (z. B. ausserhalb der
 *     Region-BBox die rio-rgbify gebaut hat)
 *   - PNG-Decode failt (sollte selten — Mapbox-RGB-Tiles sind valide PNG)
 */
async function loadTilePixels(
  archive: PMTiles,
  region: 'liguria' | 'elba',
  z: number,
  x: number,
  y: number,
): Promise<TileCacheEntry | null> {
  const key = `${region}/${z}/${x}/${y}`;
  const cached = tileCache.get(key);
  if (cached) {
    // LRU-Touch: re-insert am Ende der Map damit älteste Einträge bei
    // Eviction zuerst rausfliegen.
    tileCache.delete(key);
    tileCache.set(key, cached);
    return cached;
  }
  const inFlight = inFlightDecodes.get(key);
  if (inFlight) return inFlight;

  const promise = (async () => {
    try {
      const tile = await archive.getZxy(z, x, y);
      if (!tile || !tile.data) return null;

      // PNG-Decode via Browser-Native createImageBitmap.
      // Geht auf iOS Safari ab 14.5; auf Desktop überall. Wenn createImageBitmap
      // nicht verfügbar ist, wäre der HTMLCanvas-Fallback eine Option, aber das
      // ist mittlerweile unnötig (alle modernen Browser).
      const blob = new Blob([tile.data], { type: 'image/png' });
      const bitmap = await createImageBitmap(blob);
      const { width, height } = bitmap;

      // OffscreenCanvas wenn verfügbar (off-main-thread möglich), sonst
      // HTMLCanvasElement.
      let pixels: Uint8ClampedArray;
      const OffscreenCanvasCtor =
        typeof OffscreenCanvas !== 'undefined' ? OffscreenCanvas : null;
      if (OffscreenCanvasCtor) {
        const c = new OffscreenCanvasCtor(width, height);
        const ctx = c.getContext('2d');
        if (!ctx) throw new Error('no 2d context');
        ctx.drawImage(bitmap, 0, 0);
        pixels = ctx.getImageData(0, 0, width, height).data;
      } else {
        const c = document.createElement('canvas');
        c.width = width;
        c.height = height;
        const ctx = c.getContext('2d');
        if (!ctx) throw new Error('no 2d context');
        ctx.drawImage(bitmap, 0, 0);
        pixels = ctx.getImageData(0, 0, width, height).data;
      }
      bitmap.close?.();

      const entry: TileCacheEntry = { pixels, width, height };

      // LRU-Eviction
      if (tileCache.size >= TILE_CACHE_MAX) {
        const oldestKey = tileCache.keys().next().value;
        if (oldestKey) tileCache.delete(oldestKey);
      }
      tileCache.set(key, entry);
      return entry;
    } catch (err) {
      console.warn(
        `[dem] tile load failed ${region}/z=${z}/x=${x}/y=${y}:`,
        err instanceof Error ? err.message : err,
      );
      return null;
    } finally {
      inFlightDecodes.delete(key);
    }
  })();
  inFlightDecodes.set(key, promise);
  return promise;
}

/**
 * Mapbox-Terrain-RGB-Decode: ein Pixel (R, G, B) ergibt eine Höhe in
 * Metern. Formel ist offizieller Mapbox-Standard (rio-rgbify-Default).
 *   elev = -10000 + (R * 256 * 256 + G * 256 + B) * 0.1
 */
function rgbToElevation(r: number, g: number, b: number): number {
  return -10000 + (r * 65536 + g * 256 + b) * 0.1;
}

/**
 * Liest die Höhe an einem Pixel aus den decoded Tile-Pixels.
 * subX / subY müssen im Bereich [0, TILE_SIZE) liegen.
 *
 * Bilinear-Sampling für glattere Profile: wenn die Position zwischen
 * Pixeln liegt, mitteln wir über die 4 Nachbarn gewichtet nach Distanz.
 * Vermeidet die treppenartigen Höhen-Plateaus die man bei pure-nearest
 * sieht (auf MTB-Tracks merkbar bei steilen Hängen).
 */
function sampleTilePixel(
  entry: TileCacheEntry,
  subX: number,
  subY: number,
): number {
  const { pixels, width, height } = entry;
  const x0 = Math.max(0, Math.min(width - 1, Math.floor(subX)));
  const y0 = Math.max(0, Math.min(height - 1, Math.floor(subY)));
  const x1 = Math.min(width - 1, x0 + 1);
  const y1 = Math.min(height - 1, y0 + 1);
  const fx = subX - x0;
  const fy = subY - y0;

  const at = (px: number, py: number): number => {
    const i = (py * width + px) * 4;
    return rgbToElevation(pixels[i] ?? 0, pixels[i + 1] ?? 0, pixels[i + 2] ?? 0);
  };
  const e00 = at(x0, y0);
  const e10 = at(x1, y0);
  const e01 = at(x0, y1);
  const e11 = at(x1, y1);
  return (
    e00 * (1 - fx) * (1 - fy) +
    e10 * fx * (1 - fy) +
    e01 * (1 - fx) * fy +
    e11 * fx * fy
  );
}

/**
 * Sample-Eintragspunkt: liest die Höhe an einer einzelnen lat/lon-Position
 * aus den offline PMTiles. Returns null wenn:
 *   - Position liegt ausserhalb beider Region-Bounds
 *   - Region-PMTiles ist nicht offline registriert
 *   - Tile fehlt (Track läuft an die BBOX-Kante)
 *
 * Idempotent + safe für parallele Aufrufe — in-flight-dedup kümmert sich
 * darum dass jeder Tile nur einmal decodiert wird.
 */
export async function samplePmtilesElevation(
  lat: number,
  lon: number,
): Promise<number | null> {
  const region = resolveRegionForPosition(lat, lon);
  if (!region) return null;

  const archive = getPmtilesArchive(region.archiveUrl);
  if (!archive) return null;

  const tile = latLonToTileAndPixel(lat, lon, HILLSHADE_MAX_Z);
  if (!tile) return null;

  const entry = await loadTilePixels(archive, region.region, tile.z, tile.x, tile.y);
  if (!entry) return null;

  return sampleTilePixel(entry, tile.subX, tile.subY);
}

/**
 * Multi-Sample-API: 100 Lat/Lon-Paare auf einmal. Effizient weil:
 *   - Tile-Loads werden parallel + dedupliziert
 *   - Pixel-Sample ist O(1) pro Punkt nach Tile-Hit
 *
 * Returns null in einem Slot wenn die einzelne Sample fehlschlägt — der
 * Caller kann mit dem `lastValid`-Pattern interpolieren oder verwerfen
 * (bei zu wenig Validity).
 */
export async function sampleManyPmtilesElevations(
  positions: Array<{ lat: number; lon: number }>,
): Promise<Array<number | null>> {
  return Promise.all(positions.map((p) => samplePmtilesElevation(p.lat, p.lon)));
}

/**
 * Sind die Hillshade-PMTiles für mindestens EINE Region offline verfügbar?
 * Schneller Check für Caller die entscheiden wollen ob sie überhaupt
 * versuchen sollen vor dem MapLibre-Fallback.
 */
export function isOfflineDemAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  for (const region of ['liguria', 'elba'] as const) {
    if (getPmtilesArchive(hillshadeArchiveUrl(region))) return true;
  }
  return false;
}
